type KeyOf<T> = T extends ReadonlyArray<any> ? number | keyof T : keyof T;
type ValueOf<T, K extends KeyOf<T>> = K extends keyof T ? T[K] : T extends ReadonlyArray<infer V> ? V : never;
interface Updater<T, U = T> {
  <K extends KeyOf<U>>(key: K): Updater<T, ValueOf<U, K>>;
  map: (update: (u: U) => U) => T;
  set: (newValue: U) => T;
}

// Type-safe deep updates
// The original object is returned if the deep field's new value is equal to its current value per Object.is.
// These are use-once; don't make two separate updates using part of the same chain.
// Example usage:
// const o = { a: { b: 0, c: 1 }, d: 2 };
// updateIfChanged(o)('a')('c').map(c => c + 2); // Evaluates to { a: { b: 0, c: 3 }, d: 2 }
export const updateIfChanged = <T>(t: T): Updater<T> => {
  const reduce = <U>(u: U, update: (u: U) => T): Updater<T, U> => {
    const set = (newU: U): T => (Object.is(u, newU) ? t : update(newU));
    type Key = KeyOf<U>;
    type Value<K extends Key> = ValueOf<U, K>;
    return Object.assign(
      <K extends Key>(key: K) =>
        reduce<Value<K>>(u[key as keyof U] as Value<K>, (v: Value<K>) =>
          update(Object.assign(Array.isArray(u) ? [] : {}, u, { [key]: v })),
        ),
      { map: (updater: (u: U) => U): T => set(updater(u)), set },
    );
  };
  return reduce<T>(t, (t: T) => t);
};

const { hasOwnProperty } = Object.prototype;

// Return an object that is deeply equal to t2 while maintaining reference equality with t1 where possible.
// If t1 is deeply equal to t2, this returns t1. Otherwise, it starts with t2, but replaces sub-objects that are deeply
// equal in t1 and t2 with the sub-object from t1 (creating copies where necessary, not mutating t1 or t2).
export const updateToDeep = <T>(t1: T | undefined, t2: T): T => {
  if (Object.is(t1, t2)) {
    return t1 as T;
  }
  if (t1 == null || t2 == null || typeof t1 !== 'object' || typeof t2 !== 'object') {
    return t2;
  }
  const t2IsArray = Array.isArray(t2);
  const copy = (t2IsArray ? [] : {}) as Partial<T>;
  const t2Keys = Object.keys(t2);
  let matchesT1 = Array.isArray(t1) === t2IsArray && Object.keys(t1).length === t2Keys.length;
  let matchesT2 = true;
  for (const key of t2Keys) {
    const t1Property = t1[key as keyof T];
    const t2Property = t2[key as keyof T];
    const updatedProperty = updateToDeep(t1[key as keyof T], t2[key as keyof T]);
    copy[key as keyof T] = updatedProperty;
    if (!hasOwnProperty.call(t1, key) || !Object.is(t1Property, updatedProperty)) {
      matchesT1 = false;
    }
    if (!Object.is(t2Property, updatedProperty)) {
      matchesT2 = false;
    }
  }
  return matchesT1 ? t1 : matchesT2 ? t2 : (copy as T);
};
